import { useEffect } from "react";
import { Link } from "react-router-dom";

import { PageShell } from "../components/PageShell.js";
import { Seo } from "../components/Seo.js";

interface Quote {
  text: string;
  translation?: string;
  source?: string;
}

interface Entry {
  name: string;
  quotes: Quote[];
}

interface Group {
  title: string;
  description: string;
  entries: Entry[];
}

const GROUPS: Group[] = [
  {
    title: "World Religions & Spiritual Traditions",
    description:
      "Religious traditions across the world have long encouraged compassion, generosity, and care for others. While each expresses these values in its own unique way, they all invite us to look beyond ourselves and recognize our connection with others.",
    entries: [
      {
        name: "Hinduism",
        quotes: [
          {
            text: "Īśāvāsyam idaṁ sarvam.",
            translation: "Everything is pervaded by the Divine.",
            source: "Īśā Upaniṣad 1"
          },
          {
            text: "The wise see with equal vision a learned and humble person, a cow, an elephant, a dog, and one who is marginalized.",
            source: "Bhagavad Gītā 5:18"
          }
        ]
      },
      {
        name: "Buddhism",
        quotes: [
          {
            text: "As a mother would protect her only child with her life, so should one cultivate boundless love toward all beings.",
            source: "Metta Sutta"
          },
          {
            text: "Hatred is never appeased by hatred. By non-hatred alone is hatred appeased.",
            source: "Dhammapada 5"
          }
        ]
      },
      {
        name: "Jainism",
        quotes: [
          {
            text: "Parasparopagraho Jīvānām.",
            translation: "Living beings support one another.",
            source: "Tattvārtha Sūtra 5.21"
          }
        ]
      },
      {
        name: "Sikhism",
        quotes: [
          {
            text: "Recognize the entire human race as one.",
            source: "Guru Gobind Singh"
          },
          {
            text: "In the midst of this world, perform selfless service, and you shall find your place with the Divine.",
            source: "Guru Granth Sahib"
          }
        ]
      },
      {
        name: "Christianity",
        quotes: [
          {
            text: "Love your neighbor as yourself.",
            source: "Matthew 22:39"
          },
          {
            text: "Bear one another's burdens, and so fulfill the law of Christ.",
            source: "Galatians 6:2"
          }
        ]
      },
      {
        name: "Islam",
        quotes: [
          {
            text: "None of you truly believes until he loves for his brother what he loves for himself.",
            source: "Ṣaḥīḥ al-Bukhārī and Ṣaḥīḥ Muslim"
          },
          {
            text: "Whoever relieves a believer's distress, Allah will relieve his distress on the Day of Resurrection.",
            source: "Ṣaḥīḥ Muslim"
          }
        ]
      },
      {
        name: "Judaism",
        quotes: [
          {
            text: "Love your neighbor as yourself.",
            source: "Leviticus 19:18"
          },
          {
            text: "Whoever saves one life, it is as if they have saved an entire world.",
            source: "Mishnah Sanhedrin 4:5"
          }
        ]
      },
      {
        name: "Indigenous Wisdom",
        quotes: [
          {
            text: "We are all related.",
            source:
              "A teaching shared across many Indigenous traditions of North America"
          }
        ]
      }
    ]
  },
  {
    title: "Philosophies and Schools of Thought",
    description:
      "Many philosophical traditions, both religious and secular, arrive at similar conclusions through ethics, reason, and reflection on what enables individuals and societies to flourish.",
    entries: [
      {
        name: "Confucianism",
        quotes: [
          {
            text: "Do not impose on others what you do not wish for yourself.",
            source: "Analects 15:24"
          }
        ]
      },
      {
        name: "Stoicism",
        quotes: [
          {
            text: "What brings no benefit to the hive brings none to the bee.",
            source: "Marcus Aurelius"
          }
        ]
      },
      {
        name: "Ubuntu",
        quotes: [
          {
            text: "Umuntu ngumuntu ngabantu.",
            translation: "I am because we are."
          }
        ]
      },
      {
        name: "Humanism",
        quotes: [
          {
            text: "Human beings can live meaningful lives by using reason and compassion in the service of themselves and humanity.",
            source: "Inspired by the Amsterdam Declaration on Humanism"
          }
        ]
      },
      {
        name: "Environmental Ethics",
        quotes: [
          {
            text: "A thing is right when it tends to preserve the integrity, stability, and beauty of the biotic community.",
            source: "Aldo Leopold"
          }
        ]
      }
    ]
  },
  {
    title: "Spiritual Teachers and Reformers",
    description:
      "Throughout history, countless teachers have demonstrated that wisdom finds its fullest expression through compassion and service.",
    entries: [
      {
        name: "Mahatma Gandhi",
        quotes: [
          {
            text: "The best way to find yourself is to lose yourself in the service of others."
          }
        ]
      },
      {
        name: "Swami Vivekananda",
        quotes: [{ text: "They alone live who live for others." }]
      },
      {
        name: "Sri Ramana Maharshi",
        quotes: [{ text: "There are no others." }]
      },
      {
        name: "Mother Teresa",
        quotes: [
          {
            text: "Not all of us can do great things. But we can do small things with great love."
          }
        ]
      },
      {
        name: "The Dalai Lama",
        quotes: [
          {
            text: "If you want others to be happy, practice compassion. If you want to be happy, practice compassion."
          }
        ]
      },
      {
        name: "Martin Luther King Jr.",
        quotes: [
          {
            text: "Life's most persistent and urgent question is: 'What are you doing for others?'"
          }
        ]
      }
    ]
  },
  {
    title: "Scientists, Thinkers, and Humanitarians",
    description:
      "Modern science, philosophy, and humanitarian leadership increasingly recognize humanity's profound interconnectedness and our shared responsibility for one another.",
    entries: [
      {
        name: "Albert Einstein",
        quotes: [
          {
            text: "Our task must be to free ourselves from this prison by widening our circle of compassion to embrace all living creatures and the whole of nature."
          }
        ]
      },
      {
        name: "Albert Schweitzer",
        quotes: [
          {
            text: "I am life that wants to live, in the midst of life that wants to live."
          }
        ]
      },
      {
        name: "Jane Goodall",
        quotes: [
          {
            text: "What you do makes a difference, and you have to decide what kind of difference you want to make."
          }
        ]
      },
      {
        name: "Rabindranath Tagore",
        quotes: [
          {
            text: "I slept and dreamt that life was joy. I awoke and saw that life was service. I acted and behold, service was joy."
          }
        ]
      },
      {
        name: "Thomas Paine",
        quotes: [
          {
            text: "The world is my country, all mankind are my brethren, and to do good is my religion."
          }
        ]
      }
    ]
  }
];

export function SpiritQuotations() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <PageShell>
      <Seo
        title="The Universal Spirit of JivaSeva · JivaSeva"
        description="A growing collection of quotations, teachings, and reflections on selfless service drawn from the world's religions, philosophies, spiritual teachers, scientists, and humanitarian leaders."
        path="/spirit/quotations"
      />

      <p className="manifesto">
        <Link to="/spirit#quotations" className="inline-link">
          ← Back to The Spirit of JivaSeva
        </Link>
      </p>

      <h1 className="page-heading">The Universal Spirit of JivaSeva</h1>

      <section className="about-section">
        <p className="manifesto">
          Throughout history, people from every culture, religion, philosophy,
          and walk of life have arrived at remarkably similar insights:
          compassion, generosity, and selfless service are essential to a
          meaningful life.
        </p>
        <p className="manifesto">
          Although our languages, beliefs, and traditions differ, these voices
          remind us that we are deeply interconnected. Caring for one another is
          one of the deepest expressions of our shared humanity.
        </p>
        <p className="manifesto">
          The following selections offer just a small glimpse into this rich and
          diverse heritage.
        </p>
      </section>

      {GROUPS.map((group) => (
        <section key={group.title} className="about-section">
          <h2 className="services-category">{group.title}</h2>
          <p className="manifesto">{group.description}</p>
          {group.entries.map((entry) => (
            <div key={entry.name}>
              <h3 className="subsection-heading">{entry.name}</h3>
              {entry.quotes.map((quote, i) => (
                <blockquote key={i} className="quote">
                  <p className="quote-text">{`“${quote.text}”`}</p>
                  {quote.translation && (
                    <p className="quote-translation">{`“${quote.translation}”`}</p>
                  )}
                  {quote.source && (
                    <p className="quote-attribution">{`— ${quote.source}`}</p>
                  )}
                </blockquote>
              ))}
            </div>
          ))}
        </section>
      ))}

      <section className="about-section">
        <p className="manifesto">
          These quotations represent only a small glimpse into humanity's shared
          heritage. Though they arise from different cultures, traditions, and
          perspectives, they point toward an enduring truth: compassion, service,
          and concern for others enrich both the individual and society.
        </p>
        <p className="manifesto">
          JivaSeva celebrates this shared spirit and seeks to remove the
          practical barriers that often stand in the way of compassionate action.
          Every act of kindness—however small—is another expression of the simple
          principle that guides this initiative:
        </p>
        <p className="callout">
          <strong>Life Takes Care of Life</strong>
        </p>
      </section>

      <section className="about-section">
        <h2 className="section-heading">A Note to Our Readers</h2>
        <p className="manifesto">
          The quotations presented here are intended to inspire reflection and
          illustrate common themes found across diverse traditions and
          perspectives. They are not intended to be comprehensive or definitive
          representations of any religion, philosophy, or individual.
        </p>
        <p className="manifesto">
          If you know of a quotation, teaching, or perspective that beautifully
          expresses the spirit of JivaSeva, we warmly invite you to share it with
          us through our{" "}
          <Link to="/contact" className="inline-link">
            Contact page
          </Link>
          .
        </p>
      </section>

      <p className="manifesto">
        <Link to="/spirit#quotations" className="inline-link">
          ← Back to The Spirit of JivaSeva
        </Link>
      </p>
    </PageShell>
  );
}
